import { Component, OnInit, inject, DestroyRef, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Location } from '@angular/common';
import { Observable, of, switchMap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { User } from '@shared/entities/user';
import { Paginate } from '@shared/entities/paginate';
import { ProfileService } from '@shared/services/api/profile.service';
import { CompanyGroupUsersService } from '@shared/services/api/company-group-users.service';
import { FilterSelectedEventType } from '@shared/models/types';
import { CompanyGroupUsersFiltersService, CompanyGroupUsersFilterURL } from '@shared/services/api/company-group-users-filters.service';
import { UserFilter } from '@shared/entities/userFilter';

@Component({
  selector: 'app-company-group-users',
  templateUrl: './company-group-users.component.html',
  styleUrl: './company-group-users.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CompanyGroupUsersComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);
  private _profileService = inject(ProfileService);
  private _companyGroupUsersService = inject(CompanyGroupUsersService);
  private _userFiltersService = inject(CompanyGroupUsersFiltersService);
  private _location = inject(Location);

  users$: Observable<User[]>;
  userFilter$: Observable<UserFilter>;
  emptyFilters$ = of({});

  prevPage?: number;
  currentPage = 1;
  nextPage?: number;
  totalPages?: number;

  companyGroupName = '';

  private _companyGroupId?: string;

  constructor() {
    this.users$ = this._companyGroupUsersService.allUsers$.pipe(
      switchMap((paginated: Paginate<User>) => {
        this.prevPage = paginated.prevPage;
        this.currentPage = paginated.currentPage ?? 1;
        this.nextPage = paginated.nextPage;
        this.totalPages = paginated.totalPage;
        this._cdr.markForCheck();
        return of(paginated.data);
      })
    );
    this.userFilter$ = this._userFiltersService.all$;
  }

  ngOnInit(): void {
    this._profileService.profile$
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((profile) => {
        if (profile.companyGroupId) {
          this._companyGroupId = profile.companyGroupId;
          this._companyGroupUsersService.filters = { companyGroupId: profile.companyGroupId };
          this._userFiltersService.filters = { companyGroupId: profile.companyGroupId };
        }
        if (profile.companyGroupName) {
          this.companyGroupName = profile.companyGroupName;
        }
        this._cdr.markForCheck();
      });
  }

  onFilterSelected(filters: FilterSelectedEventType[]): void {
    const keyword = filters.find(f => f.name === 'keyword')?.selectedValue;
    const accountId = filters.find(f => f.name === 'accountId')?.selectedValue;
    const serialNumber = filters.find(f => f.name === 'serialNumber')?.selectedValue;
    const newFilters: CompanyGroupUsersFilterURL = {
      companyGroupId: this._companyGroupId
    };
    if (keyword) newFilters.keyword = keyword;
    if (accountId) newFilters.accountId = accountId;
    if (serialNumber) newFilters.serialNumber = serialNumber;
    this._companyGroupUsersService.filters = newFilters;
  }

  onPageSelected(page: number): void {
    this._companyGroupUsersService.refresh(page);
  }

  goBack(): void {
    this._location.back();
  }
}
